import { useRef, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Box, Flex, Text } from '@chakra-ui/react';

interface AboutSectionProps {
  description?: string | null;
}

const COLLAPSED_MAX_H = 78;

export function AboutSection({ description }: AboutSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  // Collapse again whenever a different profile's description loads.
  useEffect(() => {
    setIsExpanded(false);
  }, [description]);

  // Detect whether the text is taller than the collapsed height. scrollHeight
  // reports the full content height even while the element is clamped/hidden.
  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollHeight > COLLAPSED_MAX_H + 1);
  }, [description, isExpanded]);

  const showToggle = isOverflowing || isExpanded;
  const showFade = !isExpanded && isOverflowing;

  return (
    <Box px="20px" mb="20px">
      <Text as="h2" fontSize="20px" fontWeight="bold" color="#1e293b" mb="16px" letterSpacing="wide" mt="0">About</Text>
      <Box bg="white" borderRadius="20px" p="20px" boxShadow="0 6px 20px rgba(100,116,139,0.1)" border="1px solid" borderColor="#f1f5f9">
        <Box position="relative" w="full">
          <Text
            ref={descriptionRef}
            fontSize="16px"
            color="#475569"
            lineHeight="26px"
            letterSpacing="tight"
            m="0"
            whiteSpace="pre-wrap"
            overflow="hidden"
            {...(!isExpanded ? { maxH: `${COLLAPSED_MAX_H}px` } : {})}
          >
            {description || 'No description yet.'}
          </Text>
          {showFade && (
            <Box
              position="absolute"
              left="0"
              right="0"
              bottom="0"
              h="36px"
              w="full"
              background="linear-gradient(to bottom, rgba(255,255,255,0), white)"
              pointerEvents="none"
            />
          )}
        </Box>
        {showToggle && (
          <Flex
            as="button"
            alignSelf="center"
            mt="16px"
            px="14px"
            py="8px"
            gap="6px"
            borderRadius="12px"
            bg="#f8fafc"
            border="none"
            cursor="pointer"
            align="center"
            justify="center"
            _hover={{ bg: "#f1f5f9" }}
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Show less about text' : 'Show more about text'}
          >
            <Text as="span" fontSize="14px" fontWeight="semibold" color="#475569">
              {isExpanded ? 'See less' : 'See more'}
            </Text>
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </Flex>
        )}
      </Box>
    </Box>
  );
}
